import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

// ─── Color-filter helpers (pure functions, no side-effects) ──────────────────

/**
 * Lowercase, collapse spaces/underscores to hyphens, strip non-alphanumeric except hyphens.
 * @param {string} value
 * @returns {string}
 */
function slugify(value) {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Extract a clean slug from an image filename (not a full URL).
 * Strips Shopify's appended hash/variant suffix (everything from the first underscore onward).
 *
 * @example
 *   filenameSlug('hoodie-black-sleeve_14f7d26a.jpg') → 'hoodie-black-sleeve'
 *   filenameSlug('zip-hoodie-grey.webp')             → 'zip-hoodie-grey'
 *
 * @param {string} filename
 * @returns {string}
 */
function filenameSlug(filename) {
  const withoutExt = filename.replace(/\.[^.]+$/, '');
  const stripped = withoutExt.includes('_') ? withoutExt.split('_')[0] : withoutExt;
  return slugify(stripped);
}

/**
 * Derive a canonical color key from a Shopify Color option value string.
 * Maps "Steel Grey", "Washed Grey", "Light Gray" → "grey";
 *      "Jet Black", "Black" → "black";
 *      anything else → slugify(value) as-is.
 *
 * @param {string} colorValue  Human-readable color, e.g. "Steel Grey"
 * @returns {string}           Canonical key, e.g. "grey"
 */
function colorKey(colorValue) {
  const c = slugify(colorValue);
  if (c.includes('grey') || c.includes('gray')) return 'grey';
  if (c.includes('black')) return 'black';
  return c;
}

/**
 * Return true when a filename slug contains the given color key as a whole hyphen-delimited token.
 * Token matching prevents "blackout" from matching "black".
 *
 * @param {string} slug  Result of filenameSlug()
 * @param {string} key   Result of colorKey()
 * @returns {boolean}
 */
function imageMatchesColor(slug, key) {
  return slug.split('-').includes(key);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Apply color filter on initial load and after gallery replacement (replaceWith).
    // queueMicrotask lets the slideshow finish its own connectedCallback first.
    queueMicrotask(() => this.#filterSlidesByColor());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  // ─── Color-filter private methods ─────────────────────────────────────────

  /**
   * Read the human-readable value of the selected Color option from the variant picker DOM.
   * Matches the fieldset whose legend starts with `data-color-option-name` (default "Color").
   *
   * @returns {string|null}  e.g. "Steel Grey", or null if not found.
   */
  #getSelectedColorValue() {
    const colorOptionName = (this.dataset.colorOptionName || 'Color').toLowerCase();
    const section = this.closest('.shopify-section');
    if (!section) return null;

    for (const fieldset of section.querySelectorAll('fieldset[data-fieldset-index]')) {
      const legend = fieldset.querySelector('legend');
      // startsWith handles swatch legends that append the selected value after the name
      if (legend && legend.textContent.trim().toLowerCase().startsWith(colorOptionName)) {
        const checked = fieldset.querySelector('input:checked');
        // input value="{{ product_option_value | escape }}" = the human-readable color string
        if (checked) return checked.value;
      }
    }
    return null;
  }

  /**
   * Collect all canonical color keys available for this product (one per Color option value).
   * Used to classify images as color-specific vs. non-specific.
   *
   * @returns {string[]}  e.g. ["black", "grey"]
   */
  #getAllColorKeys() {
    const colorOptionName = (this.dataset.colorOptionName || 'Color').toLowerCase();
    const section = this.closest('.shopify-section');
    if (!section) return [];

    for (const fieldset of section.querySelectorAll('fieldset[data-fieldset-index]')) {
      const legend = fieldset.querySelector('legend');
      if (legend && legend.textContent.trim().toLowerCase().startsWith(colorOptionName)) {
        return Array.from(fieldset.querySelectorAll('input[type="radio"]'))
          .map((input) => colorKey(input.value))
          .filter(Boolean);
      }
    }
    return [];
  }

  /**
   * Show slides whose filename matches the selected color, hide all others.
   *
   * Decision matrix per slide:
   *   • filename contains selected color token   → show
   *   • filename contains a different color token → hide
   *   • filename contains no known color token    → show (non-specific / lifestyle image)
   *   • no filename data-attribute at all         → show (safe fallback)
   *
   * If zero slides match (images not yet renamed to convention), shows everything.
   * Navigates to the first visible slide without animation.
   *
   * @param {string} [overrideColorValue]  Skip DOM read and use this value directly.
   */
  #filterSlidesByColor(overrideColorValue) {
    const selectedColor = overrideColorValue ?? this.#getSelectedColorValue();
    if (!selectedColor) return;

    const key = colorKey(selectedColor);
    const allKeys = this.#getAllColorKeys();
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));

    let firstVisibleIndex = -1;

    slides.forEach((slide, index) => {
      const filename = slide.dataset.filename || '';
      const slug = filename ? filenameSlug(filename) : '';

      let show = true;

      if (slug && allKeys.length > 0) {
        // Determine which (if any) known color this image belongs to
        const matchedKey = allKeys.find((k) => imageMatchesColor(slug, k));
        if (matchedKey !== undefined) {
          // Color-specific image: show only if it's the selected color
          show = matchedKey === key;
        }
        // No match among allKeys → non-specific image → keep show = true
      }

      if (show) {
        slide.removeAttribute('hidden');
        if (firstVisibleIndex === -1) firstVisibleIndex = index;
      } else {
        slide.setAttribute('hidden', '');
      }
    });

    // Fallback: nothing matched (filenames don't follow the naming convention) → show all
    if (firstVisibleIndex === -1) {
      slides.forEach((slide) => slide.removeAttribute('hidden'));
      firstVisibleIndex = 0;
    }

    // Scroll to the first visible slide; queueMicrotask ensures slideshow is ready
    queueMicrotask(() => {
      this.slideshow?.select(firstVisibleIndex, undefined, { animate: false });
    });
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  /**
   * Fires immediately when the user taps a variant option (before the async section fetch).
   * Applies color filtering right away — zero flicker on iOS, Android, and desktop.
   *
   * @param {VariantSelectedEvent} _event
   */
  #handleVariantSelected = (_event) => {
    // The variant-picker.js has already updated input:checked before dispatching this event,
    // so #getSelectedColorValue() reads the correct new color from the DOM.
    this.#filterSlidesByColor();
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * The new gallery's connectedCallback will re-apply the color filter automatically.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Zooms the media gallery.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
